import {Component,OnDestroy,OnInit} from "@angular/core";
import {ActivatedRoute,Router} from "@angular/router";
import {filter,first} from "rxjs/operators";
import {TranslateService} from "@ngx-translate/core";
import {BehaviorSubject,Subscription} from "rxjs";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {Alerte,NiveauAlerte} from "@domain/common/alerte/alerte";
import {ApiRestDetailsService} from "@components/admin/parametre/api/rest/api-rest-details/api-rest-details.service";
import {Location} from "@angular/common";

/**
 * Enum des types de compte API
 */
export enum TypeCompteApi {
    PERSONNEL = "PERSONNEL",
    SERVICE = "SERVICE"
}

/**
 * Enum pour les noms des différents onglets
 */
export enum Onglets {
    COMPTE = "Compte",
    DROITS = "Droits"
}

/**
 * Page de détails d'un compte API
 *
 * @author Guillaume TRAVOT
 * @date 26/11/2025
 */
@Component({
    host: {'data-test-id': 'api-rest-details'},
    templateUrl: './api-rest-details.component.html'
})
export class ApiRestDetailsComponent implements OnInit, OnDestroy, IComponentWithRoutedTabs {
    /** Liste statique des onglets */
    public static listeOnglets: Array<PageHeaderItem> = [
        {
            code: Onglets.COMPTE,
            libelle: 'admin.parametres.api.rest.details.compte.title',
        }, {
            code: Onglets.DROITS,
            libelle: 'admin.parametres.api.rest.details.droits.title',
        }
    ];

    /** Libellé pour affichage dans le titre */
    libelleTitle: string;

    /** Liste des actions possibles */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Liste des différents onglets disponibles */
    listeTabItems: Array<PageHeaderItem>;

    /** Onglet courant */
    selectedItem: PageHeaderItem = ApiRestDetailsComponent.listeOnglets[0];

    /** Référence vers l'Enum des onglets */
    Onglets = Onglets;

    /** Index de l'onglet courant */
    selectedIndex: number = 0;

    /** Alerte à afficher lorsque le compte est verrouillé  */
    alerteCompteVerrouille: Alerte;

    /** Souscription */
    souscription: Subscription;

    /**
     * Constructeur
     */
    constructor(
        private router: Router,
        private location: Location,
        private activatedRoute: ActivatedRoute,
        private translateService: TranslateService,
        protected apiRestDetailsService: ApiRestDetailsService) {}

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Récupération de l'ID prestation dans la route
        this.activatedRoute.params.pipe(first()).subscribe(params => {
            //Sauvegarde l'ID du compte
            const idCompte: number = parseInt(params['idCompte']);

            //Chargement des données du compte API
            this.apiRestDetailsService.loadCompteApi(idCompte);
        });

        //Ajout des actions possibles
        this.listeActions.next([
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-sauvegarde',
                libelle: 'global.actions.enregistrer',
                doAction: () => this.apiRestDetailsService.saveCompteApi()
            },{
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                isVisible: () => !!this.apiRestDetailsService.compteApi?.idCompte && !this.apiRestDetailsService.compteApi?.derniereConnexion,
                doAction: () => this.apiRestDetailsService.deleteCompteApi()
            },{
                type: TypeAction.SECONDARY,
                icone: 'material-icons-outlined',
                iconeName: 'key',
                libelle: 'admin.parametres.api.rest.details.compte.nouvelleCle',
                isVisible: () => !!this.apiRestDetailsService.compteApi?.idCompte,
                doAction: () => this.apiRestDetailsService.generateNewKey()
            },{
                type: TypeAction.SECONDARY,
                icone: 'material-icons-outlined',
                iconeName: 'lock_open',
                libelle: 'admin.parametres.api.rest.details.compte.deverrouiller',
                isVisible: () => this.apiRestDetailsService.compteApi?.verrouille,
                doAction: () => this.apiRestDetailsService.unlockCompteApi()
            }
        ]);

        //Création d'une alerte dédiée
        this.alerteCompteVerrouille = new Alerte({
            niveau: NiveauAlerte.ERROR,
            message: this.translateService.instant("admin.parametres.api.rest.details.verrouille")
        });

        //Abonnement aux mises à jour du compte API
        this.souscription = this.apiRestDetailsService.compteApi$.pipe(filter(compteAPI => !!compteAPI)).subscribe(compteApi => {
            //Mise à jour du libellé
            this.libelleTitle = compteApi.libelle;

            //Si le compte dispose d'un ID
            if (!!compteApi.idCompte) {
                //Synchronisation de l'URL (utile dans le cas d'une création)
                this.location.go(`/Admin/Parametre/API/Rest/${compteApi.idCompte}`);
            }

            //Détermination des onglets accessibles
            this.listeTabItems = ApiRestDetailsComponent.listeOnglets;
        });
    }

    /**
     * Changement d'onglet
     *
     * @param selectedItem Onglet sélectionné
     */
    onSelectedItemChange(selectedItem: PageHeaderItem): void {
        if (!this.selectedItem) {
            this.selectedItem = selectedItem;
        }
        if (selectedItem?.code !== this.selectedItem?.code) {
            this.selectedItem = selectedItem;
        }
    }

    /**
     * Retour à la liste
     */
    onGoBack(): void {
        //Retour à la liste
        this.router.navigate(['Admin/Parametre/API/Rest']);
    }

    /**
     * Déstruction du composant
     */
    ngOnDestroy(): void {
        //Résiliation de la souscription
        if (this.souscription) {
            this.souscription?.unsubscribe();
        }
    }
}